import React from "react";
import PropTypes from "prop-types";
import { Paper, Typography, LinearProgress, Box } from "@mui/material";
import { Line } from "react-chartjs-2";
import { CHART_COLORS } from "./chartTheme";
import { formatCurrency } from "./formatters";

// Time-series chart card — single or multi-dataset (revenue, orders trend by status,
// customer growth, comparison trend, seasonal monthly/quarterly comparisons).
const LineChartCard = ({ title, labels = [], datasets = [], loading, height = 300, currency = false }) => {
  const hasData = labels.length > 0 && datasets.some((ds) => ds.data && ds.data.length > 0);

  const data = {
    labels,
    datasets: datasets.map((ds, index) => ({
      label: ds.label,
      data: ds.data,
      borderColor: ds.color || CHART_COLORS[index % CHART_COLORS.length],
      backgroundColor: ds.color || CHART_COLORS[index % CHART_COLORS.length],
      tension: 0.3,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", display: datasets.length > 1 },
      tooltip: currency
        ? {
            callbacks: {
              label: (context) => `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`,
            },
          }
        : undefined,
    },
    scales: currency
      ? {
          y: {
            ticks: {
              callback: (value) => formatCurrency(value),
            },
          },
        }
      : undefined,
  };

  return (
    <Paper sx={{ padding: "1rem" }}>
      <Typography sx={{ fontSize: "16px", fontWeight: 700, marginBottom: "1rem" }}>
        {title}
      </Typography>
      {loading && <LinearProgress />}
      {!loading && !hasData && (
        <Typography sx={{ color: "#6B7280", fontSize: "13px", padding: "2rem 0", textAlign: "center" }}>
          No data available for this range.
        </Typography>
      )}
      {!loading && hasData && (
        <Box sx={{ height }}>
          <Line options={options} data={data} />
        </Box>
      )}
    </Paper>
  );
};

LineChartCard.propTypes = {
  title: PropTypes.string.isRequired,
  labels: PropTypes.array,
  datasets: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      data: PropTypes.array,
      color: PropTypes.string,
    }),
  ),
  loading: PropTypes.bool,
  height: PropTypes.number,
  currency: PropTypes.bool,
};

export default LineChartCard;
